import { ComponentType } from 'react';

import { Box } from '../../common/ui/Box';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

import { SegmentedControlRadioGroup } from './components/SegmentedControlRadioGroup';
import { SegmentedControlRadioGroupItem } from './components/SegmentedControlRadioGroupItem';

type IconComponentType = ComponentType<{ width?: number; height?: number }>;

type AtLeastLabelOrIcon =
    | { label: string; Icon?: IconComponentType; count?: number; tooltipLabel?: string }
    | { label?: never; Icon: IconComponentType; count?: number; tooltipLabel?: string };

export type SegmentedControlOption<T extends string> = {
    value: T;
} & AtLeastLabelOrIcon;

export interface SegmentedControlProps<T extends string> {
    options: SegmentedControlOption<T>[];
    value: T;
    onChange: (value: T) => void;
}

export const SegmentedControl = <T extends string>({ options, onChange, value }: SegmentedControlProps<T>) => {
    return (
        <SegmentedControlRadioGroup onValueChange={onChange} value={value}>
            {options.map((option) => {
                const content = (
                    <Box display="flex" gap={4} flexWrap="nowrap">
                        {option.Icon ? <option.Icon width={16} height={16} /> : null}
                        {option.label ? option.label : null}
                        {option.count ? (
                            <Text color={option.value === value ? 'inherit' : 'foreground.subtle'} variant="action">
                                {option.count}
                            </Text>
                        ) : null}
                    </Box>
                );

                if (option.tooltipLabel) {
                    return (
                        <SegmentedControlRadioGroupItem key={option.value} value={option.value}>
                            <Tooltip label={option.tooltipLabel} showArrow>
                                <Box width="100%" height="100%">
                                    {content}
                                </Box>
                            </Tooltip>
                        </SegmentedControlRadioGroupItem>
                    );
                }

                return (
                    <SegmentedControlRadioGroupItem key={option.value} value={option.value}>
                        {content}
                    </SegmentedControlRadioGroupItem>
                );
            })}
        </SegmentedControlRadioGroup>
    );
};
